import { auth } from '../firebase/config';

// Helper seguro para variáveis de ambiente (Compatível com Vite e AI Studio)
const getEnv = (key: string, fallback: string) => {
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      // @ts-ignore
      return import.meta.env[key];
    }
  } catch (e) {
    // Silently fail
  }

  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key];
    }
  } catch (e) {
    // Silently fail
  }

  return fallback;
};

// FASE 2: Configuração das URLs do Backend (Cloud Functions)
// Se houver uma variável de ambiente VITE_API_URL configurada, usamos ela.
// Caso contrário, usamos a URL de produção padrão.
const DEFAULT_URL = "https://us-central1-meu_projeto.cloudfunctions.net";
const BASE_URL = getEnv("VITE_API_URL", DEFAULT_URL);

export const CLOUD_FUNCTIONS = {
  createCheckout: `${BASE_URL}/createCheckout`,
  cancelSubscription: `${BASE_URL}/cancelSubscription`
};

export interface CheckoutSession {
  init_point: string;
  id: string;
}

/**
 * Passo 2.2: Criar Função de Checkout
 * Envia os dados para a Cloud Function que gera o link do Mercado Pago.
 */
export const createCheckoutSession = async (user: any, planId: 'monthly' | 'quarterly'): Promise<CheckoutSession> => {
  try {
    const currentOrigin = window.location.origin;

    // Obter Token de Segurança (JWT) para provar identidade no backend
    const token = await user.getIdToken();

    const response = await fetch(CLOUD_FUNCTIONS.createCheckout, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Header de Segurança
      },
      body: JSON.stringify({
        uid: user.uid,
        email: user.email,
        planId: planId,
        returnUrl: `${currentOrigin}/?payment=success&plan=${planId}`
      })
    });

    if (!response.ok) {
      // Tenta ler o erro do JSON, se falhar, lê o texto puro
      let errorMessage = 'Falha ao conectar com o servidor de pagamento.';
      try {
        const err = await response.json();
        errorMessage = err.error || errorMessage;
      } catch (e) {
        const text = await response.text();
        if (text) errorMessage = text;
      }

      // Detecta erro específico de permissão do Google Cloud
      if (response.status === 403 || errorMessage.includes("UNAUTHORIZED")) {
        throw new Error("ERRO DE PERMISSÃO (403): O Google Cloud bloqueou o acesso. \n\nSolução: Vá no painel do Cloud Run > createCheckout > Segurança e marque 'Permitir acesso público' (Allow unauthenticated invocations).");
      }

      throw new Error(`Erro do Servidor (${response.status}): ${errorMessage}`);
    }

    const data = await response.json();

    // Validação extra conforme instruções
    if (!data.init_point) {
      throw new Error('O backend não retornou um link de pagamento válido (init_point).');
    }

    return data;
  } catch (error) {
    console.error("Erro no checkout:", error);
    throw error;
  }
};

/**
 * Função de Cancelamento
 */
export const cancelSubscription = async (userId: string, subscriptionId?: string): Promise<void> => {
  try {
    // Obter token do usuário atual via auth singleton
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error("Usuário não autenticado.");
    }

    const response = await fetch(CLOUD_FUNCTIONS.cancelSubscription, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Header de Segurança
      },
      body: JSON.stringify({ uid: userId, subscriptionId })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Erro ao cancelar assinatura');
    }
  } catch (error) {
    console.error("Erro cancelamento:", error);
    throw error;
  }
};